import { Component, HostListener, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { loadUsers } from '../../../state/admin.actions';
import { take } from 'rxjs';
import { selectUsers } from '../../../state/admin.selector';
import { AsyncPipe, NgClass } from '@angular/common';
import { UserUtils } from '../../../../../common/helpers/user-utils';
import { PaginationComponent } from '../../components/pagination/pagination.component';
import { RouterLink } from '@angular/router';
import { User } from '../../../domain/models/user/user';
import { UserStatusModalComponent } from '../../components/user-status-modal/user-status-modal.component';
import { UserService } from '../../../data/services/user.service';
import { MultiSelectDropdownComponent } from '../../components/multi-select-dropdown/multi-select-dropdown.component';
import { UserProfile } from '../../../domain/enums/user-profile';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DateUtils } from '../../../../../common/helpers/date-utils';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    FormsModule,
    AsyncPipe,
    PaginationComponent,
    RouterLink,
    NgClass,
    UserStatusModalComponent,
    MultiSelectDropdownComponent,
    TranslatePipe,
  ],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss',
})
export class UsersComponent implements OnInit {
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  selectedUser: User | null = null;
  selectedProfiles: UserProfile[] = [];
  selectedStatus: string[] = [];
  showModal: boolean = false;
  optionsDropdownOpen: string | null = null;
  profileOptions = [
    { label: 'User', value: 'User' },
    { label: 'Admin', value: 'Admin' },
    { label: 'Super Admin', value: 'SuperAdmin' },
  ];
  statusOptions: { label: string; value: string }[] = [];
  protected store = inject(Store);
  users$ = this.store.select(selectUsers);
  protected translate = inject(TranslateService);
  protected userService = inject(UserService);
  protected readonly dateUtils = inject(DateUtils);
  protected readonly userUtils = inject(UserUtils);

  ngOnInit(): void {
    this.store
      .select(selectUsers)
      .pipe(take(1))
      .subscribe((u) => {
        this.store.dispatch(
          loadUsers({
            page: u.page || 1,
            pageSize: u.pageSize || 10,
            search: u.search,
            profiles: this.selectedProfiles,
            status: this.selectedStatus,
          }),
        );
      });

    this.statusOptions = [
      {
        label: this.translate.instant('admin.users.statusOptions.active'),
        value: 'Active',
      },
      {
        label: this.translate.instant('admin.users.statusOptions.disabled'),
        value: 'Disabled',
      },
    ];

    this.translate.onLangChange.subscribe(() => {
      this.statusOptions = [
        {
          label: this.translate.instant('admin.users.statusOptions.active'),
          value: 'Active',
        },
        {
          label: this.translate.instant('admin.users.statusOptions.disabled'),
          value: 'Disabled',
        },
      ];
    });
  }

  clearFilters(): void {
    this.selectedProfiles = [];
    this.selectedStatus = [];
    this.onProfileChange(this.selectedProfiles);
    this.onStatusChange(this.selectedStatus);
  }

  onSearchChange(value: string) {
    this.store.dispatch(loadUsers({ page: 1, pageSize: 10, search: value }));
  }

  openStatusModal(user: User): void {
    this.selectedUser = { ...user };
    this.showModal = true;
  }

  closeStatusModal(): void {
    this.showModal = false;
    this.selectedUser = null;
  }

  confirmUpdateStatus(): void {
    if (!this.selectedUser) return;

    this.userService.updateUserStatus(this.selectedUser.id).subscribe(() => {
      this.store
        .select(selectUsers)
        .pipe(take(1))
        .subscribe((u) => {
          this.store.dispatch(
            loadUsers({
              page: u.page,
              pageSize: u.pageSize,
              search: u.search ?? '',
            }),
          );
        });
      this.closeStatusModal();
    });
  }

  onPageChange(page: number): void {
    this.store
      .select(selectUsers)
      .pipe(take(1))
      .subscribe((u) => {
        this.store.dispatch(
          loadUsers({
            page,
            pageSize: u.pageSize,
            search: u.search,
          }),
        );
      });
  }

  onPageSizeChange(newSize: number): void {
    this.store
      .select(selectUsers)
      .pipe(take(1))
      .subscribe((u) => {
        this.store.dispatch(
          loadUsers({
            page: 1,
            pageSize: newSize,
            search: u.search,
          }),
        );
      });
  }

  onStatusChange(status: string[] | string): void {
    this.selectedStatus = Array.isArray(status) ? status : [status];

    this.store
      .select(selectUsers)
      .pipe(take(1))
      .subscribe((u) => {
        this.store.dispatch(
          loadUsers({
            page: 1,
            pageSize: u.pageSize,
            search: u.search,
            status: this.selectedStatus,
            profiles: this.selectedProfiles,
          }),
        );
      });
  }

  onProfileChange(profiles: string[] | string): void {
    const normalizedProfiles = Array.isArray(profiles) ? profiles : [profiles];
    this.selectedProfiles = normalizedProfiles.map((p) => p as UserProfile);

    this.store
      .select(selectUsers)
      .pipe(take(1))
      .subscribe((u) => {
        this.store.dispatch(
          loadUsers({
            page: 1,
            pageSize: u.pageSize,
            search: u.search,
            status: this.selectedStatus,
            profiles: this.selectedProfiles,
          }),
        );
      });
  }

  onSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
  }

  toggleOptionsDropdown(userId: string) {
    this.optionsDropdownOpen =
      this.optionsDropdownOpen === userId ? null : userId;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;

    if (
      !target.closest('.options-button') &&
      !target.closest('.options-dropdown')
    ) {
      this.optionsDropdownOpen = null;
    }
  }
}
